import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const CustomerWorkspaceContext = createContext(null);

const DEFAULT_PANEL_META = Object.freeze({
  title: 'Workspace',
  timelineHref: null,
  unreadCount: 0,
});

export function CustomerWorkspaceProvider({ children }) {
  const [rightSlot, setRightSlotState] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [detailSplitLayout, setDetailSplitLayoutState] = useState(false);
  const [panelMeta, setPanelMetaState] = useState(DEFAULT_PANEL_META);

  const setRightSlot = useCallback((node) => {
    setRightSlotState(node);
    if (!node) {
      setMobileRailOpen(false);
      setRightPanelOpen(false);
      setPanelMetaState(DEFAULT_PANEL_META);
    }
  }, []);

  const setPanelMeta = useCallback((next) => {
    setPanelMetaState(next ? { ...DEFAULT_PANEL_META, ...next } : DEFAULT_PANEL_META);
  }, []);

  const setDetailSplitLayout = useCallback((value) => {
    setDetailSplitLayoutState(Boolean(value));
  }, []);

  const value = useMemo(
    () => ({
      rightSlot,
      setRightSlot,
      rightPanelOpen,
      setRightPanelOpen,
      mobileRailOpen,
      setMobileRailOpen,
      detailSplitLayout,
      setDetailSplitLayout,
      panelMeta,
      setPanelMeta,
    }),
    [
      rightSlot,
      setRightSlot,
      rightPanelOpen,
      mobileRailOpen,
      detailSplitLayout,
      setDetailSplitLayout,
      panelMeta,
      setPanelMeta,
    ],
  );

  return <CustomerWorkspaceContext.Provider value={value}>{children}</CustomerWorkspaceContext.Provider>;
}

export function useCustomerWorkspace() {
  const ctx = useContext(CustomerWorkspaceContext);
  if (!ctx) {
    throw new Error('useCustomerWorkspace must be used within CustomerWorkspaceProvider');
  }
  return ctx;
}

/**
 * Registers optional right-panel content for the authenticated workspace template.
 * Wide (≥ lg) + slot + open: detail split — bounded main scroll + Ant `Sider` rail above the footer.
 * Narrow: drawer + float button.
 */
export function useCustomerRightRail(slot) {
  const { setRightSlot } = useCustomerWorkspace();
  useEffect(() => {
    setRightSlot(slot ?? null);
    return () => setRightSlot(null);
  }, [slot, setRightSlot]);
}

/**
 * Registers metadata for the right-rail header trigger (timeline link, unread count, title).
 * Pass a memoized object so the header trigger doesn't re-render on every parent render.
 */
export function useCustomerRightRailMeta(meta) {
  const { setPanelMeta } = useCustomerWorkspace();
  useEffect(() => {
    setPanelMeta(meta ?? null);
    return () => setPanelMeta(null);
  }, [meta, setPanelMeta]);
}
